import { Button, fromTheme, styled, Text, View } from 'lib_ui-primitives';
import { metadata } from 'lib_ui-services';
import PropTypes from 'prop-types';
import { Fragment, memo, createElement as rc, useContext } from 'react';
import { Route } from 'react-router';
import { useTheme } from 'styled-components';
import HNode from '../../../HNode';
import ActiveRecord from '../../contextProviders/ActiveRecord';
import ContinuousScanPage from '../../ContinuousScanPage';
import SyncDialog from '../../dialog/SyncDialog';
import NotificationIcon from '../../notification/NotificationIcon';
import RouteContainer from '../../RouteContainer';
import NavbarContextProvider, { NavbarContext } from '../NavBarContextProvider.gl2025';
import doChildrenRequireSidebar from './doChildrenRequireSidebar';
import LayoutTitle from './LayoutTitle';

const LeftSideNavAppLayoutContainer = styled(View).attrs({ name: 'LeftSideNavAppLayout' })`
    flex-direction: column;
    align-items: stretch;
    margin: 0;
    padding: 0;
    height: 100%;
    width: 100%;
`;
LeftSideNavAppLayoutContainer.displayName = 'LeftSideNavAppLayout';

const LeftToRight = styled(View)`
    flex-direction: row;
    align-items: stretch;
    margin: 0;
    padding: 0;
`;

const TopBar = styled(LeftToRight).attrs({ name: 'top-bar' })`
    background-color: ${({ theme }) => theme.titleBar.backgroundColor};
    flex-grow: 0;
    flex-shrink: 0;
    height: ${({ theme }) => theme.titleBar.height}px;
    overflow: hidden;
`;
TopBar.displayName = 'TopBar';

const SidebarAndMainContent = styled(LeftToRight).attrs({ name: 'sidebar-and-main-content' })`
    position: relative;
    width: 100%;
    flex-grow: 1;
    flex-shrink: 1;
`;
SidebarAndMainContent.displayName = 'SidebarAndMainContent';

const HamburgerArea = styled(View).attrs({ name: 'hamburger-area' })`
    flex-basis: auto;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    width: ${({ theme, navLocked }) =>
        navLocked && !theme.mobile ? theme.sideBar.width : theme.titleBar.hamburgerMinWidth}px;
    padding: ${fromTheme('titleBar', 'hamburgerPadding')};
    flex-grow: 0;
    flex-shrink: 0;
    transition: width 0.3s ease-in-out;
    border-right: ${({ theme }) =>
        theme.mobile ? 'none' : `${theme.sideBar.borderWidth}px solid ${theme.sideBar.borderColor}`};
    border-bottom: ${({ theme }) =>
        theme.mobile ? `${theme.titleBar.bottomBorderWidth}px solid ${theme.titleBar.borderColor}` : 'none'};
`;
HamburgerArea.displayName = 'HamburgerArea';

const HamburgerButton = styled(Button).attrs({ name: 'hamburger-button' })`
    margin: 0;

    &:focus-visible,
    &:focus,
    &:active,
    &:hover {
        margin: 0 !important;
    }
`;

const TitleArea = styled(View).attrs({ name: 'title-area' })`
    flex-basis: auto;
    flex-direction: row;
    align-items: center;
    padding: 0;
    flex-grow: 1;
    flex-shrink: 1;
    min-width: 0;
    border-bottom: ${({ theme }) => `${theme.titleBar.bottomBorderWidth}px solid ${theme.titleBar.borderColor}`};
`;
TitleArea.displayName = 'TitleArea';

const MainContainer = styled(View).attrs({ name: 'main-container' })`
    flex-basis: auto;
    flex-grow: 1;
    flex-shrink: 1;
    flex-direction: column;
    ${({ theme }) => {
        return theme.mobile
            ? `padding: ${theme.mainContainer.padding}px ${theme.mainContainer.padding}px 0
        ${theme.mainContainer.padding}px`
            : '';
    }}
`;
MainContainer.displayName = 'MainContainer';

const ButtonBarHeader = styled(View).attrs({ name: 'right-header' })`
    flex-basis: auto;
    flex-grow: 0;
    flex-shrink: 0;
    flex-direction: row;
    align-items: center;
    margin-right: ${fromTheme('titleBar', 'marginRight')};

    & > button,
    & > button:focus,
    & > button:hover {
        margin-left: ${fromTheme('titleBar', 'buttonMargin')}px;
        margin-right: ${fromTheme('titleBar', 'buttonMargin')}px;
    }
`;

const TitleHeader = styled(View).attrs({ name: 'title-header' })`
    flex-basis: 0;
    flex-direction: row;
    align-items: center;
    flex-grow: 1;
    flex-shrink: 1;
    min-width: 0;
    overflow: hidden;
    white-space: nowrap;
    padding: 0 ${fromTheme('titleBar', 'paddingHorizontal')};
`;

let DragAffordance = styled(View).attrs({ name: 'drag-affordance' })`
    position: absolute;
    top: ${fromTheme('sideBar', 'drag', 'top')};
    left: 0;
    padding: ${fromTheme('sideBar', 'drag', 'padding')};
    background-color: ${fromTheme('sideBar', 'backgroundColor')};
    border: ${({ theme }) => `${theme.sideBar.borderWidth}px solid ${theme.sideBar.borderColor}`};
    border-top-right-radius: ${fromTheme('sideBar', 'drag', 'radius')};
    border-bottom-right-radius: ${fromTheme('sideBar', 'drag', 'radius')};
    z-index: ${({ theme }) => theme.zindex.Drawer + 1};
`;

const FOUR_VERTICAL_DOTS = '\u205E\u205E';
const Handle = styled(Text)`
    ${fromTheme('sideBar', 'drag', 'font')};
    color: ${fromTheme('outerMenus', 'fontColor')};
`;

function LeftSideNavAppLayout(props) {
    const { hNode } = props;

    //HACK, MDC3.0 needs to be updated to properly manage a splash page.
    //Right now, it relies on a top-level menu entry/page with the title "SPLASH", "somewhere" in the menu list
    //Ideally it should get it's own place slightly apart in the menu, or better yet, on a page of it's own.
    const splashScreenIndex = hNode.children[0]?.children?.findIndex(child => child.title === 'SPLASH');
    if (splashScreenIndex > -1) {
        const [splashPageNavHeading] = hNode.children[0].children.splice(splashScreenIndex, 1);
        const splashNode = {
            hNodeType: 'SplashContextAppLayout',
            hNodeTypeGroup: 'appLayout',
            namespace: splashPageNavHeading.children?.[0]?.defaultNamespace || 'metadata',
            relation: splashPageNavHeading.children?.[0]?.defaultRelation || 'useCaseDetail',
            splashPage: splashPageNavHeading,
            children: [hNode]
        };
        return rc(HNode, { ...props, hNode: splashNode, children: undefined });
    }

    // prettier-ignore
    return rc(LeftSideNavAppLayoutContainer, null,
        rc(NavbarContextProvider, null,
            rc(InnerLeftSideNavAppLayout, props)
        )
    );
}

export function InnerLeftSideNavAppLayout(props) {
    const topMenuHNodes = metadata.getTopMenu();
    const { id, hNode, currentRoute, _excludeSideBar = false } = props;
    // when there is only 1 page in this role, we might not need to show the sidebar at all regardless.
    const excludeSideBar = _excludeSideBar || !doChildrenRequireSidebar(hNode.children?.[0]);
    const theme = useTheme();
    const headerButtonSize = theme?.titleBar?.buttonSize;

    const navBarContext = useContext(NavbarContext);
    const { ready, locked: navLocked, toggleLockState } = navBarContext;

    if (!ready) return null;

    if (theme.smallMobile) {
        return rc(
            SidebarAndMainContent,
            null,
            !excludeSideBar &&
                rc(DragAffordance, { onClick: () => toggleLockState() }, rc(Handle, null, FOUR_VERTICAL_DOTS)),
            !excludeSideBar && props.children,
            //main content
            rc(MainContainer, null,
                rc(Route, { path: ['/continuous-scan', '/g/:groupNumber/continuous-scan'] },
                    rc(View, { style: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1, elevation: 1 } },
                        rc(ContinuousScanPage)
                    )
                ),
                rc(RouteContainer, { hNode, currentRoute })
            )
        );
    }

    // prettier-ignore
    return rc(Fragment, null,
         //topBar
        rc(TopBar, null,
            !excludeSideBar && rc(HamburgerArea, { navLocked },
                rc(HamburgerButton, { id: `btn${id}`,
                    title: 'Toggle Navigation',
                    alt: 'Menu',
                    buttonStyle:'round',
                    onClick: toggleLockState,
                    icon: navLocked ? 'collapse' : 'expand',
                    color: 'transparent',
                    iconColor:theme.outerMenus?.fontColor,
                    variant:'tertiary',
                    destructive: false,
                    size: headerButtonSize

                })
            ),
            rc(TitleArea, null,
                rc(TitleHeader, null,
                    rc(LayoutTitle)
                ),
                rc(ButtonBarHeader, null,
                    rc(NotificationIcon, { id:`notif${id}`, size: headerButtonSize }),
                    rc(ActiveRecord, {namespace: 'security',relation: 'profile'},
                        topMenuHNodes.map(topMenuHNode =>
                            rc(HNode, { hNode: { showDarkModeToggle: true, buttonSize: headerButtonSize, ...topMenuHNode }, key:topMenuHNode.id, currentRoute })
                        )
                    ),
                    rc(SyncDialog, { id:`sync${id}`})
                )
            )
        ),
        // sidebar and main content
        rc(SidebarAndMainContent, null,
             // Sidebar (nav_SideNav) with NavHeaders
             // cannot be nested more, see comment in sidenav.browser.js
            !excludeSideBar && props.children,
            //main content
            rc(MainContainer, null,
                rc(Route, { path: ['/continuous-scan', '/g/:groupNumber/continuous-scan'] },
                    rc(View, { style: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1, elevation: 1 } },
                        rc(ContinuousScanPage)
                    )
                ),
                rc(RouteContainer, { hNode, currentRoute })
            )
        )
    );
}
InnerLeftSideNavAppLayout.propTypes = {
    id: PropTypes.string,
    children: PropTypes.array,
    hNode: PropTypes.object,
    currentRoute: PropTypes.object,
    _excludeSideBar: PropTypes.bool
};

LeftSideNavAppLayout.propTypes = {
    children: PropTypes.array,
    hNode: PropTypes.object,
    skipSideBar: PropTypes.bool
};

export default memo(LeftSideNavAppLayout);
